/**
 * Daily Performance and ETF Performance — one widget, two column sets, over a
 * list that is the preset's plus the member's own (`resolvedList`). The label
 * is the list's when it has one ("Dow 30"), else the symbol store's name.
 *
 * ETF Performance has a *Table | Race* view: Race is Sector Race's `RaceView`
 * over the same resolved list, one race per category when grouped by Category.
 */
import type { CSSProperties } from "react";
import type { BriefListItem } from "@qufolio/model";
import { fetchBriefPerf, type PerfPayload, type PerfRow } from "../../api/daily-brief";
import { WIDGET_META } from "../../lib/brief-board";
import { useIsNarrow } from "../../lib/use-narrow";
import { Chip } from "./cells";
import { PerfTable, type Column, type Row, type Sort } from "./PerfTable";
import { RaceControls, RaceSubtitle, RaceView, useRaceData, useRaceSettings } from "./RaceView";
import { RACE_NARROW_COLS } from "./SectorRaceWidget";
import { useBriefData } from "./useBriefData";
import { DataState, WidgetCard, useWidgetSetting, type WidgetProps } from "./WidgetCard";

export interface PerfLine extends Row {
  symbol: string;
  label: string;
  category?: string;
  perf?: PerfRow;
}

const f = (pick: (p: PerfRow) => number | null) => (r: PerfLine) => (r.perf ? pick(r.perf) : null);

const YOURS_TAG: CSSProperties = {
  font: "11px ui-monospace, monospace", border: "1px solid var(--qf-accent)", borderRadius: 4,
  padding: "0 5px", color: "var(--qf-accent)", marginLeft: 6,
};
const labelCell = (r: PerfLine) => (
  <>
    <span style={{ color: "var(--qf-text-secondary)" }}>{r.label}</span>
    {r.yours && <span style={YOURS_TAG}>yours</span>}
  </>
);

/** Symbol, label, Close, 1D %, Open, High, Low, % from open, 5D, Range %, Closing range. */
export const DAILY_COLUMNS: ReadonlyArray<Column<PerfLine>> = [
  { key: "symbol", label: "Symbol", kind: "symbol", value: (r) => r.symbol },
  { key: "label", label: "Name", kind: "text", value: (r) => r.label, render: labelCell },
  { key: "close", label: "Close", kind: "price", value: f((p) => p.close) },
  { key: "chg1", label: "1D %", kind: "bar", value: f((p) => p.chg1) },
  { key: "open", label: "Open", kind: "price", value: f((p) => p.open) },
  { key: "high", label: "High", kind: "price", value: f((p) => p.high) },
  { key: "low", label: "Low", kind: "price", value: f((p) => p.low) },
  { key: "fromOpen", label: "From open", kind: "bar", value: f((p) => p.fromOpen) },
  { key: "chg5", label: "5D", kind: "bar", value: f((p) => p.chg5) },
  { key: "rangePc", label: "Range %", kind: "pctPlain", value: f((p) => p.rangePc) },
  { key: "closeRange", label: "Close rng", kind: "range", value: f((p) => p.closeRange) },
];

/** Symbol, ETF name, % from open, 1D %, 5D %, 20D %, 50D %, Closing range. */
export const ETF_COLUMNS: ReadonlyArray<Column<PerfLine>> = [
  { key: "symbol", label: "Symbol", kind: "symbol", value: (r) => r.symbol },
  { key: "label", label: "ETF name", kind: "text", value: (r) => r.label, render: labelCell },
  { key: "fromOpen", label: "From open", kind: "bar", value: f((p) => p.fromOpen) },
  { key: "chg1", label: "1D %", kind: "bar", value: f((p) => p.chg1) },
  { key: "chg5", label: "5D %", kind: "bar", value: f((p) => p.chg5) },
  { key: "chg20", label: "20D %", kind: "bar", value: f((p) => p.chg20) },
  { key: "chg50", label: "50D %", kind: "bar", value: f((p) => p.chg50) },
  { key: "closeRange", label: "Close rng", kind: "range", value: f((p) => p.closeRange) },
];

export interface PerfTableWidgetProps extends WidgetProps {
  list: { items: BriefListItem[]; categories: string[] };
  /** Symbols the member added themselves, drawn with the "yours" mark. */
  yours: ReadonlySet<string>;
  /** Opens ✎ list; absent on a read-only board. */
  onEditList?: () => void;
  /** Race view: opens `SYM/SPY` in the linked chart. */
  onOpenSymbol?: (symbol: string) => void;
}

export function PerfTableWidget({ widget, onSettings, onRemove, list, yours, onEditList, onOpenSymbol }: PerfTableWidgetProps) {
  const etf = widget.type === "etf";
  const phone = useIsNarrow();
  const [groupBy, setGroupBy] = useWidgetSetting<"category" | "none">(widget, "groupBy", "category", onSettings);
  const [view, setView] = useWidgetSetting<"table" | "race">(widget, "view", "table", onSettings);
  const race = etf && view === "race";
  const raceSettings = useRaceSettings(widget, onSettings);
  // ETF Performance opens ranked by 1D (the mock's "1D % ▾"); Daily in the list's own order.
  const [sort, setSort] = useWidgetSetting<Sort | null>(
    widget, "sort", etf ? { field: "chg1", desc: true } : null, onSettings,
  );
  const symbols = list.items.map((i) => i.symbol);
  // Only the view on screen asks: a table does not fetch the race, nor the race the table.
  const state = useBriefData<PerfPayload>(
    symbols.length && !race ? `perf:${symbols.join(",")}` : null,
    () => fetchBriefPerf(symbols),
  );
  const raceState = useRaceData(symbols, raceSettings.freq, race);
  const categoryOf = new Map(list.items.map((i) => [i.symbol.toUpperCase(), i.category]));

  const bySymbol = new Map((state.data?.rows ?? []).map((r) => [r.symbol, r]));
  const rows: PerfLine[] = list.items.map((i) => {
    const perf = bySymbol.get(i.symbol);
    return {
      id: i.symbol, symbol: i.symbol, label: i.label || perf?.name || "", category: i.category,
      perf, yours: yours.has(i.symbol),
    };
  });

  return (
    <WidgetCard
      title={WIDGET_META[widget.type].title}
      live={race ? raceState.data?.live : state.data?.live}
      onRemove={onRemove}
      subtitle={race ? <RaceSubtitle /> : undefined}
      controls={
        <>
          {etf && (
            <>
              <Chip on={!race} onClick={() => setView("table")}>Table</Chip>
              <Chip on={race} onClick={() => setView("race")}>Race</Chip>
              {race && <RaceControls settings={raceSettings} />}
              <span style={{ color: "var(--qf-text-muted)", fontSize: 13 }}>group by</span>
              <Chip on={groupBy === "category"} onClick={() => setGroupBy("category")}>Category</Chip>
              <Chip on={groupBy === "none"} onClick={() => setGroupBy("none")}>none</Chip>
            </>
          )}
          {onEditList && <Chip onClick={onEditList}>✎ list</Chip>}
        </>
      }
    >
      {symbols.length === 0 ? (
        <div style={{ color: "var(--qf-text-muted)" }}>No symbols in this list.</div>
      ) : race ? (
        <DataState state={raceState}>
          {(data) => (
            <RaceView
              data={data}
              settings={raceSettings}
              groups={
                groupBy === "category"
                  ? { category: (s) => categoryOf.get(s.toUpperCase()), order: list.categories }
                  : undefined
              }
              narrow={phone || widget.w < RACE_NARROW_COLS}
              onOpenSymbol={onOpenSymbol}
            />
          )}
        </DataState>
      ) : (
        <DataState state={state}>
          {() => (
            <PerfTable
              columns={etf ? ETF_COLUMNS : DAILY_COLUMNS}
              rows={rows}
              sort={sort}
              onSort={setSort}
              groups={etf && groupBy === "category" ? { category: (r) => r.category, order: list.categories } : undefined}
            />
          )}
        </DataState>
      )}
    </WidgetCard>
  );
}
